import { GET_FACETS } from '@/graphql/queries';
import { districtVar, flyerboardAllFiltersVar, subscriptionsVar } from '@/graphql/state';
import { GetFacetsResponse, OwningTypeEnum } from '@/graphql/types';
import { useQuery, useReactiveVar } from '@apollo/client';
import { css } from '@emotion/css';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FilterOption, OptionType } from '@/components/FlyerboardFilters/FilterOption';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import JarFlyerboardContext from '@/context/JarFlyerboardContext';
import {
  useChildGrades,
  useCombinedCategories,
  useGoogleTranslator,
  useCategories,
  useSchools,
  useURLSearchParams,
} from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getGradeRange } from '@/utils/getGradeRange';
import { useIsMobile } from '@/hooks';
import {
  COMBINED_CATEGORIES_CONFIG,
  COMBINED_ORGANIZATIONS_CATEGORIES,
  COMBINED_ORGANIZATIONS_ID,
  COMBINED_SPORTS_CATEGORIES,
  COMBINED_SPORTS_ID,
  COMBINED_VOLUNTEER_CATEGORIES,
  COMBINED_VOLUNTEER_ID,
  JAR_CATEGORIES_ORDER,
} from './jar.constants';
import { JarFilterDrawer } from './JarFilterDrawer';
import { JarFilterModal } from './JarFilterModal';
import { AccessibleDivButton } from '@/components';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import AuthenticationContext from '@/context/AuthenticationContext';
import { SubscriptionTypeEnum } from '@/config/constants';

interface JarFilterProps {
  children: React.ReactNode;
}

type FiltersState = {
  cost: string[];
  provider: string[];
  types: string[];
  subscriptions: { id: string; type: string }[];
};

const defaultFilters: FiltersState = {
  cost: [],
  provider: [],
  types: [],
  subscriptions: [],
};

export const JarFilter: React.FC<JarFilterProps> = ({ children }) => {
  const {
    setDrawerFilters,
    filters: jarFilters,
    drawerFilters,
    districtId,
  } = useContext(JarFlyerboardContext);
  const { search } = useLocation();
  const isMobile = useIsMobile();
  const { audienceId, audienceType } = useURLSearchParams();
  const { community: userCommunity } = useContext(UserPreferencesContext);
  const { isAuthenticated } = useContext(AuthenticationContext);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const viewResultsTranslatedLabel = useGoogleTranslator(`View # results`);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [gradeFilter, setGradeFilter] = useState<{ from?: string; to?: string }>({});
  const { lowGrade, highGrade, grades } = useChildGrades();
  const { allCategories } = useCategories();
  const district = useReactiveVar(districtVar);
  const { schoolsIds, schools } = useSchools();
  const flyerboardAllFilters = useReactiveVar(flyerboardAllFiltersVar);

  const { trackEvent } = useAnalytics();

  const trackingFrom = {
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: 'jar',
    stage_from: TrackingStage.discovery,
  };

  useEffect(() => {
    if (!showFilter) return;

    setFilters(prev => {
      const newFilters: FiltersState = { ...prev };

      newFilters.cost = drawerFilters.isCommunityFree === true ? ['community_free'] : [];
      if (drawerFilters.owningType === OwningTypeEnum.district) {
        newFilters.provider = ['district'];
      } else if (drawerFilters.owningType === OwningTypeEnum.organization) {
        newFilters.provider = ['organization'];
      } else {
        newFilters.provider = [];
      }
      const typesFromParams = searchParams.get('types');
      if (typesFromParams) {
        newFilters.types = typesFromParams.split(',');
      }
      const schoolIdsFromParams = searchParams.get('schoolIds');
      if (schoolIdsFromParams) {
        newFilters.subscriptions = schoolIdsFromParams
          .split(',')
          .map(id => ({ id: id, type: 'school' }));
      }

      return newFilters;
    });

    const fromGrade = searchParams.get('fromGrade');
    const toGrade = searchParams.get('toGrade');

    if (fromGrade && toGrade) {
      setGradeFilter({
        from: fromGrade,
        to: toGrade,
      });
    } else {
      setDefaultGrades();
    }
  }, [showFilter, drawerFilters.isCommunityFree, drawerFilters.owningType, searchParams]);

  useEffect(() => {
    if (filters.cost.length > 0 && filters.provider.length > 0) {
      setFilters(prev => ({
        ...prev,
        provider: [],
      }));
    }
  }, [filters.cost]);

  const queryInput = useMemo(() => {
    const queryFilters = { ...jarFilters };

    const { types, cost, provider } = filters;
    let categories = [...types];

    if (types.includes(COMBINED_VOLUNTEER_ID)) {
      categories = [
        ...types.filter(t => t !== COMBINED_VOLUNTEER_ID),
        ...COMBINED_VOLUNTEER_CATEGORIES,
      ];
    }

    if (types.includes(COMBINED_SPORTS_ID)) {
      categories = [
        ...categories.filter(t => t !== COMBINED_SPORTS_ID),
        ...COMBINED_SPORTS_CATEGORIES,
      ];
    }

    if (types.includes(COMBINED_ORGANIZATIONS_ID)) {
      categories = [
        ...categories.filter(t => t !== COMBINED_ORGANIZATIONS_ID),
        ...COMBINED_ORGANIZATIONS_CATEGORIES,
      ];
    }

    queryFilters.categories = categories;

    if (provider.length > 0) {
      const owningType = OwningTypeEnum[provider[0]];
      queryFilters.owningType = owningType;
    } else {
      queryFilters.owningType = undefined;
    }

    const schoolFilters = filters.subscriptions
      .filter(s => s.type === 'school')
      .map(s => Number(s.id));
    if (isAuthenticated) {
      queryFilters.schoolIds = schoolFilters.length === 0 ? schoolsIds : schoolFilters;
    } else {
      if (audienceType === SubscriptionTypeEnum.school) {
        queryFilters.schoolIds = [Number(audienceId)];
      } else {
        queryFilters.schoolIds = [];
      }
    }

    queryFilters.isCommunityFree = cost.length > 0;
    if (gradeFilter.from && gradeFilter.to) {
      const grades = getGradeRange(gradeFilter.from, gradeFilter.to).map(g => g.id.toString());
      queryFilters.grades = grades;
    }
    const { lat, lng } = userCommunity?.location || {};

    return {
      filters: {
        ...queryFilters,
        districtId: district?.districtId || Number(districtId),
        schoolIds: queryFilters.schoolIds,
        communityLocation: isAuthenticated && userCommunity?.location ? { lat, lng } : undefined,
        communityRadius:
          isAuthenticated && userCommunity?.distance ? userCommunity.distance : undefined,
        totalsOnly: true,
        includeExpandedCommunity: isAuthenticated
          ? queryFilters.schoolIds.length === schoolsIds.length
          : false,
      },
    };
  }, [
    flyerboardAllFilters,
    filters,
    gradeFilter,
    schoolsIds,
    userCommunity,
    isAuthenticated,
    audienceType,
    audienceId,
  ]);

  const { data, previousData, loading } = useQuery<GetFacetsResponse>(GET_FACETS, {
    variables: {
      input: queryInput,
    },
    skip: flyerboardAllFilters === null,
  });

  const facets = data?.getFacets.facets ?? previousData?.getFacets.facets;
  const amount = data?.getFacets.pagination.total ?? previousData?.getFacets.pagination.total ?? 0;

  const { generateCombinedCategoryOptions } = useCombinedCategories({ facets });

  const viewResultsLabel = useMemo(
    () => viewResultsTranslatedLabel.replace('#', amount.toString()),
    [amount]
  );

  const setDefaultGrades = () => {
    setGradeFilter({
      from: lowGrade,
      to: highGrade,
    });
  };

  const generateProviderOptions = () => {
    const providerMap = {
      district: 'School',
      organization: 'Community',
    };

    const defaultProviderOptions = [
      { value: 'district', description: providerMap.district, amount: 0, disabled: false },
      { value: 'organization', description: providerMap.organization, amount: 0, disabled: false },
    ];

    if (!facets || !facets.owningType) return defaultProviderOptions;

    const owningTypeMap = Object.fromEntries(
      facets.owningType.map(({ key, count }) => [key, count])
    );

    const isCostSelected = filters.cost.length > 0;

    return defaultProviderOptions.map(opt => {
      const isChecked = filters.provider.includes(opt.value);
      let disabled = (owningTypeMap[opt.value] ?? 0) === 0;
      if (isCostSelected && !isChecked) {
        disabled = true;
      }
      return {
        ...opt,
        amount: owningTypeMap[opt.value] ?? 0,
        disabled,
      };
    });
  };

  const generateCostOption = () => {
    if (!facets) return [];
    return [
      {
        value: 'community_free',
        description: 'Free',
        amount,
        disabled: false,
      },
    ];
  };

  const generateTypesOptions = () => {
    if (!facets) return [];

    const categoryOptions: Array<OptionType> = [];
    const addedIds = new Set();

    const combinedOptions = generateCombinedCategoryOptions;
    combinedOptions.forEach(option => {
      categoryOptions.push(option);
      const config = Object.values(COMBINED_CATEGORIES_CONFIG).find(
        c => c.label === option.description
      );
      if (config) {
        config.categories.forEach(cat => addedIds.add(cat));
      }
    });

    allCategories.forEach(category => {
      if (addedIds.has(category.id)) return;

      categoryOptions.push({
        description: category.title,
        value: category.id,
        disabled: false,
      });
      addedIds.add(category.id);
    });

    return categoryOptions.sort((a, b) => {
      const aIndex = JAR_CATEGORIES_ORDER.indexOf(a.value);
      const bIndex = JAR_CATEGORIES_ORDER.indexOf(b.value);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setDefaultGrades();

    setDrawerFilters({
      owningType: undefined,
      isCommunityFree: undefined,
      grades: undefined,
      categories: undefined,
      schoolIds: [],
    });

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('filters');
    newSearchParams.delete('fromGrade');
    newSearchParams.delete('toGrade');
    newSearchParams.delete('types');
    newSearchParams.delete('quickFilter');
    newSearchParams.delete('schoolIds');
  };

  const viewResults = () => {
    const { owningType, categories, isCommunityFree, grades, schoolIds } = queryInput.filters;
    setDrawerFilters({
      categories,
      owningType,
      isCommunityFree,
      grades,
      schoolIds,
    });
    setShowFilter(false);

    trackEvent({
      event: TrackingEvents.filterFlyerSet,
      event_detail: EventDetails.filterPanel,
      stage: TrackingStage.discovery,
      ...trackingFrom,
    });
  };

  const handleToggleOption = (section: string, value: string, subscriptionType?: string) => {
    setFilters(prev => {
      if (section === 'subscriptions') {
        const currentSubscriptions = prev.subscriptions;
        const subscriptionToToggle = { id: value, type: subscriptionType || 'school' };

        const isSelected = currentSubscriptions.some(s => s.id === value);
        const newSubscriptions = isSelected
          ? currentSubscriptions.filter(s => s.id !== value)
          : [...currentSubscriptions, subscriptionToToggle];

        return { ...prev, subscriptions: newSubscriptions };
      } else {
        const arr = prev[section] as string[];
        const newArr = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
        return { ...prev, [section]: newArr };
      }
    });
  };

  const renderSubscriptionList = useCallback(() => {
    const filterName = 'subscriptions';
    return schools.map(({ id, name, type }) => (
      <FilterOption
        key={id}
        description={name}
        value={id}
        filterName={filterName}
        checked={filters.subscriptions.some(s => s.id === id)}
        handleToggle={() => () => handleToggleOption(filterName, id, type)}
        disabled={false}
      />
    ));
  }, [schools, filters.subscriptions, handleToggleOption]);

  const handleGradeChange = (field: 'from' | 'to', value?: string) => {
    setGradeFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const onToggleFilter = (state: boolean) => {
    setShowFilter(state);
  };

  const disableClearAllButton =
    Object.values(filters).every(arr => arr.length === 0) &&
    (!gradeFilter.from ||
      !gradeFilter.to ||
      (gradeFilter.from === lowGrade && gradeFilter.to === highGrade));

  // Shared props for both drawer and modal
  const filterProps = {
    isOpen: showFilter,
    onToggle: onToggleFilter,
    filters,
    setFilters,
    gradeFilter,
    setGradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
    lowGrade,
    highGrade,
    setDefaultGrades,
    renderSubscriptionList,
    subscriptionsLength: schools.length,
  };

  return (
    <>
      <AccessibleDivButton onClick={() => onToggleFilter(true)} aria-label="Toggle filter">
        {children}
      </AccessibleDivButton>
      {isMobile ? <JarFilterDrawer {...filterProps} /> : <JarFilterModal {...filterProps} />}
    </>
  );
};

